import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { strings } from '@/shared/strings';
import { EmptyState } from '@/ui/feedback/EmptyState';
import { SkeletonCase } from '@/ui/feedback/Loading';
import { GoBackIcon } from '@/ui/icons';
import { Button } from '@/ui/primitives/Button';
import { readFailure } from './clientRefusal';
import styles from './ClientProfile.module.css';

/** The way back from a profile to the list it belongs to. */
export interface BackTarget {
  to: string;
  label: string;
}

/**
 * The link at the head of a profile, back to its list. A link and not a
 * button: it goes somewhere, so it opens in a new tab when asked to and a
 * screen reader says where it leads.
 */
export function BackLink({ to, label }: BackTarget) {
  return (
    <Link className={styles.back} to={to}>
      <GoBackIcon /> {label}
    </Link>
  );
}

/** A profile on its way: the way back at once, and the shape of the cards to come. */
export function ProfileLoading({ back, label }: { back: BackTarget; label: string }) {
  return (
    <div>
      <BackLink {...back} />
      <SkeletonCase label={label} />
    </div>
  );
}

/**
 * A profile whose record is not there: an address somebody mistyped, or a
 * record deleted since the link was made. Said plainly, with the way back.
 */
export function ProfileNotFound({
  back,
  icon,
  heading,
  detail,
}: {
  back: BackTarget;
  icon: ReactNode;
  heading: string;
  detail: string;
}) {
  return (
    <div>
      <BackLink {...back} />
      <EmptyState icon={icon} heading={heading} detail={detail} />
    </div>
  );
}

/** A profile that could not be read for any other reason: why, and another try. */
export function ProfileFailed({
  back,
  error,
  onRetry,
}: {
  back: BackTarget;
  /** What the read failed with, said in the server's words when it refused. */
  error: unknown;
  onRetry: () => void;
}) {
  return (
    <div>
      <BackLink {...back} />
      <p className={styles.error} role="alert">
        {readFailure(error)}
        <Button onClick={onRetry}>{strings.errors.retry}</Button>
      </p>
    </div>
  );
}

/**
 * One figure of a record, over what it counts. With `onPress` a button that
 * opens what it counts: the lab's request of 17 October 2026, item 1.
 */
export function Stat({ label, value, onPress }: { label: string; value: number; onPress?: () => void }) {
  const content = (
    <>
      <b>{value.toLocaleString()}</b>
      <span>{label}</span>
    </>
  );
  if (onPress === undefined) return <div className={styles.stat}>{content}</div>;
  return (
    <button type="button" className={`${styles.stat} ${styles.statPress}`} onClick={onPress}>
      {content}
    </button>
  );
}
